"use client";

import { useCallback, useState, useMemo } from "react";
import { ReportParentContentEnum } from "@/app/types/typeConfig/report";
import {
  FeedbackTypeEnum,
  IMConfigTypeEnum,
  OriginType,
} from "@/app/types/typeConfig/chatQuestion";
import { useTranslations } from "@/app/lib/LocaleProvider";
import { useMessageStore } from "@/app/store";
import useAIBehaviorCollect from "@/app/hooks/robot/useAIBehaviorCollect";
import {
  createUuid,
  copyToClipboard,
  createSessionId,
} from "@/app/utils/string";
import {
  HeaderType,
  MessageSender,
  MessageType,
  RobotMessageType,
} from "@/app/store/types";
import { Toast } from "@douyinfe/semi-ui";
import {
  FeedbackPropsType,
  questionFeedback,
} from "@/app/api/robot/chatContent";
import { useCurrentUser } from "@/app/lib";
import { AIAnswerKnowledgeId } from "@/const/issueType";
import { isApp } from "@/app/bridge";

export type FeedbackState = "none" | "liked" | "disliked";

export interface ParentContentInfo {
  parentContentId?: string;
  parentContent?: string;
  parentContentType?: ReportParentContentEnum;
}

export interface UseAIFooterFeedbackOptions {
  parentContentInfo?: ParentContentInfo;
  requestId?: string;
  configType: IMConfigTypeEnum;
  content?: string;
  onLike?: () => void;
  onDislike?: () => void;
  streamingId?: string;
}

export function useAIFooterFeedback({
  parentContentInfo = {},
  requestId,
  configType,
  content,
  onLike,
  onDislike,
  streamingId,
}: UseAIFooterFeedbackOptions) {
  const { setRobotMessage } = useMessageStore();
  const { onLikeCollect, onDislikeCollect } = useAIBehaviorCollect(streamingId);
  const t = useTranslations();
  const [feedbackState, setFeedbackState] = useState<FeedbackState>("none");
  const user = useCurrentUser();

  const iconUrls = useMemo(
    () => ({
      like:
        feedbackState === "liked"
          ? "/images/like2-visited.svg"
          : "/images/like2.svg",
      dislike:
        feedbackState === "disliked"
          ? "/images/dislike2-visited.svg"
          : "/images/dislike2.svg",
    }),
    [feedbackState],
  );

  const handleDislike = useCallback(() => {
    if (feedbackState !== "none") return;
    setFeedbackState("disliked");
    onDislikeCollect(
      {
        parentContentId: (parentContentInfo.parentContentId as string) || "",
        parentContent: (parentContentInfo.parentContent as string) || "",
        parentContentType:
          (parentContentInfo.parentContentType as ReportParentContentEnum) ||
          ReportParentContentEnum.DEFAULT,
      },
      requestId,
    );
    // setRobotMessage({
    //   content: "",
    //   timestamp: Date.now(),
    //   streaming: false,
    //   clientId: createUuid(),
    //   type: MessageType.Text,
    //   headerType: HeaderType.MESSAGE,
    //   senderType: MessageSender.ROBOT,
    //   messageType: RobotMessageType.ROBOT_FEEDBACK,
    //   senderName: "智能助手",
    //   robotData: {
    //     feedback: "dislike",
    //     requestId: requestId,
    //     parentContentInfo,
    //     configType,
    //   },
    // });
    onDislike?.();
  }, [
    feedbackState,
    onDislike,
    requestId,
    parentContentInfo,
    configType,
    onDislikeCollect,
    setRobotMessage,
  ]);

  const handleLike = useCallback(() => {
    if (feedbackState !== "none") return;
    setFeedbackState("liked");

    const sessionId = createSessionId();
    const params: FeedbackPropsType = {
      userId: user?.cusId || sessionId,
      feedbackClass: FeedbackTypeEnum.like,
      feedback: "",
      knowledgeId: AIAnswerKnowledgeId,
      sessionId,
      origin: isApp() ? OriginType.app : OriginType.web,
      requestId: requestId || "",
      extendField4: streamingId || "",
    };
    try {
      questionFeedback(params);
    } catch (error) {
      console.error(error);
    }
    onLikeCollect(
      {
        parentContentId: (parentContentInfo.parentContentId as string) || "",
        parentContent: (parentContentInfo.parentContent as string) || "",
        parentContentType:
          (parentContentInfo.parentContentType as ReportParentContentEnum) ||
          ReportParentContentEnum.DEFAULT,
      },
      requestId,
    );
    onLike?.();
  }, [
    feedbackState,
    onLike,
    requestId,
    parentContentInfo,
    configType,
    onLikeCollect,
    setRobotMessage,
  ]);

  const handleCopy = useCallback(async () => {
    if (!content) return;
    const tempDiv = document.createElement("div");
    tempDiv.innerHTML = content;
    const textContent = tempDiv.textContent || tempDiv.innerText || "";
    if (!textContent.trim()) return;
    const success = await copyToClipboard(textContent);
    if (success) {
      Toast.success(t.tips.copySuccess);
    }
  }, [content, t.tips.copySuccess]);

  return {
    feedbackState,
    iconUrls,
    handleLike,
    handleDislike,
    handleCopy,
  };
}
